<template>
  <div v-if="shouldRender" class="team-chat-dock" @keydown.esc="closeFocused">
    <button
      type="button"
      class="mobile-chat-tab"
      :aria-expanded="mobileOpen"
      aria-label="Open active chats"
      @click="openMobileDock"
    >
      <span class="material-symbols-outlined" aria-hidden="true">forum</span>
      <span>Chats</span>
      <strong v-if="unreadTotal > 0">{{ unreadTotal > 99 ? "99+" : unreadTotal }}</strong>
    </button>

    <div class="desktop-dock" aria-label="Active chats">
      <div class="chat-tabs">
        <button
          v-for="participant in visibleParticipants"
          :key="participant.id"
          type="button"
          class="chat-tab"
          :class="{ active: teamChatUi.focusedChatId === participant.id }"
          @click="focusChat(participant.id)"
        >
          <span class="tab-avatar" aria-hidden="true">{{ initialsFor(participant) }}</span>
          <span>{{ participantLabel(participant) }}</span>
          <strong v-if="unreadFor(participant.id) > 0">
            {{ unreadFor(participant.id) > 99 ? "99+" : unreadFor(participant.id) }}
          </strong>
        </button>
      </div>

      <section
        v-if="focusedParticipant && !isMinimized(focusedParticipant.id)"
        class="dock-window"
        role="region"
        :aria-label="`Docked messages with ${participantLabel(focusedParticipant)}`"
      >
        <div class="dock-toolbar">
          <button
            type="button"
            aria-label="Minimize chat"
            @click="toggleMinimizedTeamChat(focusedParticipant.id)"
          >
            <span class="material-symbols-outlined" aria-hidden="true">remove</span>
          </button>
          <button
            type="button"
            aria-label="Close chat"
            @click="closeTeamChat(focusedParticipant.id)"
          >
            <span class="material-symbols-outlined" aria-hidden="true">close</span>
          </button>
        </div>
        <TeamChatPanel
          :participant="focusedParticipant"
          @close="closeTeamChat(focusedParticipant.id)"
          @summary-updated="applyUnreadSummary"
        />
      </section>
    </div>

    <div
      v-if="mobileOpen"
      class="mobile-sheet"
      role="dialog"
      aria-modal="false"
      aria-label="Active chats"
    >
      <div class="mobile-sheet-tabs">
        <button
          v-for="participant in visibleParticipants"
          :key="participant.id"
          type="button"
          class="mobile-sheet-tab"
          :class="{ active: teamChatUi.focusedChatId === participant.id }"
          @click="focusChat(participant.id)"
        >
          {{ participantLabel(participant) }}
          <strong v-if="unreadFor(participant.id) > 0">{{ unreadFor(participant.id) }}</strong>
        </button>
      </div>
      <TeamChatPanel
        v-if="focusedParticipant"
        :participant="focusedParticipant"
        @close="mobileOpen = false"
        @summary-updated="applyUnreadSummary"
      />
    </div>
  </div>
</template>

<script>
import socket from "../../socket";
import store from "../../store/index.js";
import TeamChatPanel from "../router-pages/loggedin/TeamChatPanel.vue";
import {
  closeTeamChat,
  hydrateTeamChatUi,
  openTeamChat,
  setFocusedTeamChat,
  teamChatUi,
  toggleMinimizedTeamChat,
} from "../../utils/teamChatUi";

export default {
  name: "TeamChatDock",
  components: { TeamChatPanel },
  data() {
    return {
      teamChatUi,
      mobileOpen: false,
      unreadSummary: { total: 0, byUserId: {} },
      socketBound: false,
    };
  },
  computed: {
    activeUser() {
      return store.state.activeUser || {};
    },
    activeLabId() {
      return this.canonicalId(this.activeUser.activeLabId) || this.canonicalId(this.activeUser.labId);
    },
    allParticipants() {
      const byId = new Map();
      for (const user of [...(store.state.users || []), ...(store.state.noAdminUsers || [])]) {
        const id = this.canonicalId(user);
        if (!id || id === this.canonicalId(this.activeUser) || this.isSystemUser(user)) continue;
        byId.set(id, { ...user, id });
      }
      return Array.from(byId.values());
    },
    participantMap() {
      return new Map(this.allParticipants.map((participant) => [participant.id, participant]));
    },
    visibleChatIds() {
      const ids = new Set();
      this.teamChatUi.activeChatIds.forEach((id) => ids.add(id));
      Object.entries(this.unreadSummary.byUserId || {}).forEach(([id, count]) => {
        if (Number(count) > 0) ids.add(id);
      });
      return Array.from(ids);
    },
    visibleParticipants() {
      return this.visibleChatIds.map((id) => (
        this.participantMap.get(id) || { id, name: "Team member" }
      ));
    },
    focusedParticipant() {
      const focusedId = this.teamChatUi.focusedChatId || this.visibleParticipants[0]?.id || null;
      return focusedId
        ? this.visibleParticipants.find((participant) => participant.id === focusedId) || null
        : null;
    },
    unreadTotal() {
      return Number(this.unreadSummary.total || 0);
    },
    shouldRender() {
      return Boolean(
        this.activeUser?.id &&
          this.activeUser.role !== "temp" &&
          this.$route.name !== "team-members" &&
          this.$route.name !== "messages" &&
          this.visibleParticipants.length > 0
      );
    },
  },
  watch: {
    "activeUser.id": {
      immediate: true,
      handler() {
        hydrateTeamChatUi(this.canonicalId(this.activeUser), this.activeLabId);
      },
    },
    activeLabId() {
      hydrateTeamChatUi(this.canonicalId(this.activeUser), this.activeLabId);
      this.fetchUnreadSummary();
    },
    visibleParticipants(participants) {
      if (!participants.length) {
        setFocusedTeamChat(null);
        this.mobileOpen = false;
        return;
      }
      if (!this.teamChatUi.focusedChatId || !participants.some((participant) => participant.id === this.teamChatUi.focusedChatId)) {
        setFocusedTeamChat(participants[0].id);
      }
    },
    "$route.name"() {
      this.mobileOpen = false;
    },
  },
  mounted() {
    this.fetchUnreadSummary();
    this.bindSocket();
    document.addEventListener("keydown", this.handleDocumentKeydown);
  },
  beforeUnmount() {
    this.unbindSocket();
    document.removeEventListener("keydown", this.handleDocumentKeydown);
  },
  methods: {
    closeTeamChat,
    toggleMinimizedTeamChat,
    canonicalId(value) {
      if (!value) return "";
      if (typeof value === "string") return value;
      return value.id || "";
    },
    isSystemUser(user) {
      return (
        typeof user?.email === "string" &&
        user.email.endsWith("@subangstrom.local") &&
        user.email.startsWith("system+")
      );
    },
    initialsFor(user) {
      return String(user?.name || user?.email || "?")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join("") || "?";
    },
    participantLabel(user) {
      return user?.name || user?.email || "Team member";
    },
    unreadFor(participantId) {
      return Number(this.unreadSummary.byUserId?.[participantId] || 0);
    },
    isMinimized(participantId) {
      return this.teamChatUi.minimizedChatIds.includes(participantId);
    },
    focusChat(participantId) {
      openTeamChat(participantId);
      this.mobileOpen = true;
    },
    openMobileDock() {
      if (!this.teamChatUi.focusedChatId && this.visibleParticipants[0]) {
        setFocusedTeamChat(this.visibleParticipants[0].id);
      }
      this.mobileOpen = true;
    },
    closeFocused() {
      if (this.mobileOpen) {
        this.mobileOpen = false;
        return;
      }
      if (this.focusedParticipant) toggleMinimizedTeamChat(this.focusedParticipant.id);
    },
    handleDocumentKeydown(event) {
      if (event.key === "Escape") this.closeFocused();
    },
    applyUnreadSummary(summary) {
      if (!summary) return;
      this.unreadSummary = {
        total: Number(summary.total || 0),
        byUserId: summary.byUserId || {},
      };
    },
    fetchUnreadSummary() {
      if (!this.activeUser?.id) return;
      const request = new Request(`${store.state.hostname}/api/v1/messages/unread-summary`, {
        credentials: "include",
        method: "GET",
        headers: { "content-type": "application/json" },
      });
      fetch(request)
        .then((resp) => resp.json())
        .then((data) => {
          if (data.status === "fail" || data.status === "error") throw new Error(data.message);
          this.applyUnreadSummary(data.data?.data);
        })
        .catch((err) => console.log(err));
    },
    handleMessageEvent(payload) {
      if (payload?.unreadSummary) this.applyUnreadSummary(payload.unreadSummary);
      else this.fetchUnreadSummary();
    },
    bindSocket() {
      if (this.socketBound) return;
      this.socketBound = true;
      socket.on("team-message:new", this.handleMessageEvent);
      socket.on("team-message:unread-summary", this.applyUnreadSummary);
    },
    unbindSocket() {
      socket.off("team-message:new", this.handleMessageEvent);
      socket.off("team-message:unread-summary", this.applyUnreadSummary);
    },
  },
};
</script>

<style scoped>
.team-chat-dock {
  position: fixed;
  right: max(1rem, env(safe-area-inset-right));
  bottom: max(1rem, env(safe-area-inset-bottom));
  z-index: 90;
  color: #172033;
  font-family: Montserrat, Inter, system-ui, sans-serif;
}

.desktop-dock {
  display: grid;
  justify-items: end;
  gap: 0.55rem;
}

.chat-tabs {
  display: flex;
  justify-content: flex-end;
  gap: 0.4rem;
  max-width: min(780px, calc(100vw - 2rem));
  overflow-x: auto;
  padding: 0.15rem;
}

.chat-tab,
.mobile-chat-tab,
.mobile-sheet-tab,
.dock-toolbar button {
  border: 1px solid #d9e2ec;
  background: #fff;
  color: #172033;
  font: inherit;
  cursor: pointer;
  box-shadow: 0 8px 18px rgb(15 23 42 / 0.12);
}

.chat-tab {
  display: inline-grid;
  grid-template-columns: 26px minmax(0, 8rem) auto;
  align-items: center;
  gap: 0.38rem;
  min-height: 38px;
  max-width: 13rem;
  padding: 0 0.5rem;
  border-radius: 999px;
}

.chat-tab.active {
  border-color: #8fb3dc;
  box-shadow:
    0 8px 18px rgb(15 23 42 / 0.14),
    0 0 0 3px rgb(51 92 139 / 0.12);
}

.chat-tab span:nth-child(2),
.mobile-sheet-tab {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tab-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 999px;
  background: #dbeafe;
  color: #335c8b;
  font-size: 0.66rem;
  font-weight: 900;
}

.chat-tab strong,
.mobile-chat-tab strong,
.mobile-sheet-tab strong {
  min-width: 20px;
  height: 20px;
  padding: 0 0.32rem;
  border-radius: 999px;
  background: #dc2626;
  color: #fff;
  font-size: 0.68rem;
  font-weight: 900;
  line-height: 20px;
  text-align: center;
}

.dock-window {
  position: relative;
  width: min(420px, calc(100vw - 2rem));
  height: min(620px, calc(100dvh - 8rem));
  min-height: 430px;
  overflow: hidden;
  border: 1px solid #cbd7e6;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 22px 50px rgb(15 23 42 / 0.24);
}

.dock-toolbar {
  position: absolute;
  top: 0.55rem;
  right: 0.85rem;
  z-index: 3;
  display: flex;
  gap: 0.28rem;
}

.dock-toolbar button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 7px;
  box-shadow: none;
}

.dock-toolbar .material-symbols-outlined {
  font-size: 17px;
}

.dock-window :deep(.team-chat-panel) {
  height: 100%;
}

.dock-window :deep(.chat-header) {
  grid-template-columns: 40px minmax(0, 1fr);
  padding-right: 4.6rem;
}

.dock-window :deep(.icon-command) {
  display: none;
}

.dock-window :deep(.chat-thread) {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 1rem 0.85rem 1.1rem;
  scrollbar-color: #9fb3c8 transparent;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
}

.dock-window :deep(.chat-composer) {
  flex: 0 0 auto;
  padding: 0.85rem;
}

.dock-window :deep(.composer-shell) {
  gap: 0.45rem;
}

.dock-window :deep(.send-message-button) {
  min-width: 68px;
  padding: 0 0.75rem;
}

.mobile-chat-tab,
.mobile-sheet {
  display: none;
}

@media (max-width: 760px) {
  .team-chat-dock {
    right: 0;
    bottom: 0;
    left: 0;
    display: grid;
    justify-items: center;
    pointer-events: none;
  }

  .desktop-dock {
    display: none;
  }

  .mobile-chat-tab {
    display: inline-flex;
    pointer-events: auto;
    align-items: center;
    gap: 0.35rem;
    min-height: 44px;
    margin-bottom: max(0.65rem, env(safe-area-inset-bottom));
    padding: 0 0.85rem;
    border-radius: 999px;
    font-weight: 900;
  }

  .mobile-chat-tab .material-symbols-outlined {
    color: #335c8b;
    font-size: 20px;
  }

  .mobile-sheet {
    position: fixed;
    inset: auto 0 0 0;
    display: flex;
    pointer-events: auto;
    height: min(82dvh, 720px);
    flex-direction: column;
    overflow: hidden;
    border: 1px solid #cbd7e6;
    border-bottom: 0;
    border-radius: 12px 12px 0 0;
    background: #fff;
    box-shadow: 0 -18px 44px rgb(15 23 42 / 0.24);
  }

  .mobile-sheet-tabs {
    display: flex;
    gap: 0.35rem;
    overflow-x: auto;
    padding: 0.55rem 0.65rem;
    border-bottom: 1px solid #d9e2ec;
    background: #f8fbff;
  }

  .mobile-sheet-tab {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    max-width: 11rem;
    min-height: 32px;
    padding: 0 0.55rem;
    border-radius: 999px;
    box-shadow: none;
    font-size: 0.78rem;
    font-weight: 900;
  }

  .mobile-sheet-tab.active {
    border-color: #8fb3dc;
    background: #eef6ff;
  }
}
</style>
